import React, { Component } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import values from 'lodash/values';
import isEqual from 'lodash/isEqual';

// Components
import OMPropertiesList from '../../OMComponents/OMPropertiesList/OMPropertiesList';

// Helpers
import { language } from '../../../constants';
import MapboxService from '../../../services/MapboxService/MapboxService';
import {
  addMultitouchGestures,
  removeMultitouchGestures
} from '../../../services/MapboxService/helpers';
import {
  MAP_TYPES,
  PINS_LAYOUT_ID, PINS_SOURCE_ID, pinsLayoutConfig,
  requiredMapIcons
} from '../../../services/MapboxService/constants';
// styles
import * as styles from './CompanyMap.scss';

class DynamicCompanyMap extends Component {
  componentDidMount() {
    this._isMounted = true;
    this.initDynamicMap();
  }

  componentWillReceiveProps(newProps) {
    const localeIsChanged = (newProps.locale && newProps.locale !== this.props.locale);
    const pinsTypeIsChanged = (newProps.pinsType && newProps.pinsType !== this.props.pinsType);

    if (this.map && localeIsChanged) {
      MapboxService.changeMapLocale(this.map, newProps.locale, this.handleLocaleChange);
    }

    if (this.map && pinsTypeIsChanged) {
      this.popup && this.popup.remove();
      this.popup = null;
      this.fitPropertiesToMapBounds(newProps.groupedProperties);
      this.renderMapPins(newProps.groupedProperties);
    }

    if (!isEqual(newProps.reviewedPins, this.props.reviewedPins)) {
      this.findReviewedPinsThatShouldBeRepainted(newProps.reviewedPins).forEach(MapboxService.setReviewedIcon);
    }
  }

  componentWillUnmount() {
    this._isMounted = false;
    this.clearMap();
    MapboxService.cleanup();
  }

  getMapRef = (ref) => { this.mapContainer = ref; }

  propertyCellClickHandler = (preloadData) => {
    const pid = preloadData.slug || preloadData.propertyId;
    this.props.openDetails({}, pid);
  };

  toggleFavorite = () => {
    // TODO add favorites logic
  }

  handleMapLoad = () => {
    this.fitPropertiesToMapBounds();
    this.renderMapPins();
    this.map.on('click', this.handleMapClick);
    this.map.on('mousemove', this.handleMapMouseMove);
  }

  handleMapClick = (evt) => {
    if (!this.map) { return; }

    const features = this.map.queryRenderedFeatures(evt.point, {
      layers: [PINS_LAYOUT_ID]
    });

    if (!features.length) { return; }

    const feature = features[0];

    const ids = JSON.parse(get(feature, 'properties.ids', '[]'));
    const coordinates = get(feature, 'geometry.coordinates', []);
    const zIndex = get(feature, 'properties.zIndex');
    this.openInfoWindow(ids, coordinates, zIndex);
  };

  handleMapMouseMove = (evt) => {
    if (!this.map) { return; }

    const features = this.map.queryRenderedFeatures(evt.point, {
      layers: [PINS_LAYOUT_ID]
    });

    this.map.getCanvas().style.cursor = features.length ? 'pointer' : '';
  }

  handleLocaleChange = () => {
    this.reInitMapIcons();
    this.renderMapPins();
  }

  reInitMapIcons = () => {
    const icons = requiredMapIcons[MAP_TYPES.COMPANY];
    icons.forEach(icon => MapboxService.initIconToMap(this.map, icon));
  }

  initDynamicMap = (locale = this.props.locale) => {
    this.map = null;
    this.initializeDynamicMap(locale);
  };

  initializeDynamicMap = () => {
    MapboxService.createMapInstance(
      this.mapContainer,
      { ...MapboxService.getMapOptions('company') },
      this.props.locale,
      requiredMapIcons[MAP_TYPES.COMPANY]
    ).then((map) => {
      if (!map) { return; }

      if (!this._isMounted) {
        map.remove();
        return;
      }

      this.map = map;
      this.map.once('load', this.handleMapLoad);
      this.map.touchZoomRotate.disableRotation();

      if (this.props.isMobile) {
        addMultitouchGestures(this.map);
      }
    }).catch(error => console.warn('Failed to initialize company map.', error));
  };

  openInfoWindow = (ids, coordinates, zIndex) => {
    if (!ids || !ids.length) { return; }
    const properties = ids.map(id => get(this.props, ['propertiesIdsMap', id], {}));
    const content = this.createPinContent(properties);

    this.props.setPinAsReviewed(ids[0]);
    this.currentPinData = {
      id: ids[0],
      zIndex
    };

    this.renderMapPins();

    this.popup = MapboxService.createInfoWindow(content)
      .setLngLat(coordinates)
      .addTo(this.map);

    MapboxService.locateInfoWindow(this.map, coordinates, this.popup);
  }

  fitPropertiesToMapBounds = (groupedProperties = this.props.groupedProperties) => {
    const bounds = MapboxService.createBounds();
    const coordinates = Object.keys(groupedProperties).map(key => key.split('|').reverse());

    coordinates.forEach(item => bounds.extend(item));

    if (coordinates.length) {
      this.map.fitBounds(bounds, { padding: 70 });
    }
  }

  switchToDynamicMap = () => {
    this.setState({ isDynamicMap: true });
    this.initDynamicMap();
  };

  clearPins = () => {
    if (!this.map || !this.map.getSource(PINS_SOURCE_ID)) return null;

    return this.map.removeSource(PINS_SOURCE_ID);
  };

  clearMap = () => {
    if (!this.map) { return; }
    this.map.off('click', this.handleMapClick);
    this.map.off('mousemove', this.handleMapMouseMove);
    if (this.props.isMobile) {
      removeMultitouchGestures(this.map);
    }
    this.map.remove();
    this.map = null;
  };

  createPinContent = (properties) => {
    const { locale } = this.props;
    const multiple = (properties.length > 1);
    const lang = (properties[0].address[locale]) ? locale : language.fallback;
    const address = properties[0].address[lang];
    const shouldHideHouseNumber = properties.some(item => !get(item, `address[${lang}].house_number`));
    const houseNumber = (shouldHideHouseNumber) ? ', ' : ` ${address.house_number}, `;

    const addressString = (multiple) ? (
      <div className={styles.addressMultiPin}>
        {`${address.street_name}${houseNumber}${address.city_name}`}
      </div>
    ) : null;

    return (
      <div
        className={(multiple) ? styles.multipleCellWrap : ''}
      >
        {addressString}
        <OMPropertiesList
          properties={properties}
          isMobile={this.props.isMobile}
          locale={locale}
          translate={this.props.translate}
          className={multiple ? 'multiPinList' : ''}
          openDetailsPopup={this.propertyCellClickHandler}
          toggleFavorite={this.toggleFavorite}
          needScroll={multiple}
          mapPropertyCell
          isRtl={this.props.isRtl}
          overrideCurrency={this.props.overrideCurrency}
        />
      </div>
    );
  };

  formatPropertyPins = (propertiesArray) => {
    let isReviewed = false;
    const type = 'property';

    if (get(propertiesArray, '[0].address.location')) {
      const ids = propertiesArray.map(item => item.id);
      const { lat, lon } = propertiesArray[0].address.location;

      isReviewed = ids.some(id => this.props.reviewedPins.includes(id));
      const shouldMoveOnTop = this.currentPinData && ids.includes(this.currentPinData.id);
      const zIndex = this.currentPinData && (shouldMoveOnTop
        ? this.currentPinData.zIndex + 1
        : this.currentPinData.zIndex);

      return [lat, lon, {
        ids,
        type,
        reviewed: isReviewed,
        number: ids.length,
        propertiesArray,
        zIndex: zIndex || 1
      }];
    }

    return [];
  }

  createPropertyPin = (propertiesArray) => {
    let propertyPin;
    let isReviewed = false;
    const type = 'property';

    if (get(propertiesArray, '[0].address.location')) {
      const ids = propertiesArray.map(item => item.id);
      const { lat, lon } = propertiesArray[0].address.location;

      isReviewed = ids.some(id => this.props.reviewedPins.includes(id));

      propertyPin = MapboxService.createPin(lat, lon, {
        ids,
        type,
        reviewed: isReviewed,
        number: ids.length,
        propertiesArray
      });

      const pinContent = this.createPinContent(propertiesArray);
      const infoWindow = MapboxService.createInfoWindow(pinContent);

      propertyPin.setPopup(infoWindow);

      const handleInfoWindowOpen = () => MapboxService.setReviewedIcon(propertyPin);
      infoWindow.on('open', handleInfoWindowOpen);
    }
    return propertyPin;
  };

  findReviewedPinsThatShouldBeRepainted = reviewedPins => (this.pins || []).filter(
    pin => pin.ids.some(id => reviewedPins.includes(id))
  );

  renderMapPins = (groupedPins = this.props.groupedProperties) => {
    if (!this.map) return null;

    const pinsData = values(groupedPins).map(this.formatPropertyPins);
    const currentSource = this.map.getSource(PINS_SOURCE_ID);

    if (currentSource) {
      const geoJsonPinsData = MapboxService.createGeoJsonPinsData(pinsData);
      currentSource.setData(geoJsonPinsData);
    } else {
      const geoJsonPins = MapboxService.createGeoJsonPins(pinsData);
      MapboxService.applyPinsGeoJson(this.map, geoJsonPins);
    }

    return this.map;
  };

  render() {
    return (
      <div
        className={styles.dynamicMap}
        ref={this.getMapRef}
      />
    );
  }
}

DynamicCompanyMap.propTypes = {
  translate: PropTypes.func,
  locale: PropTypes.string,
  isMobile: PropTypes.bool,
  isRtl: PropTypes.bool,
  pinsType: PropTypes.string,
  groupedProperties: PropTypes.object,
  propertiesIdsMap: PropTypes.object,
  reviewedPins: PropTypes.array,
  setPinAsReviewed: PropTypes.func,
  openDetails: PropTypes.func,
  overrideCurrency: PropTypes.string
};

export default DynamicCompanyMap;
